/**
 * BIMI card with Verified Mark Certificate (VMC) detail.
 *
 * BIMI renders a sender's logo in the inbox. Gmail/Apple require a VMC — an
 * X.509 cert from DigiCert or Entrust that carries the mark. When the scan
 * fetched and parsed a VMC, we surface its provenance: who issued it, who holds
 * the mark, when it expires, and whether it clears the provider gates (approved
 * CA + logotype extension + not expired).
 *
 * Shared by the authed detail page and the public report. The vmc* fields are
 * absent on older scans, so the detail block only renders when a VMC parsed.
 */
import { BadgeCheck, Image as ImageIcon, ShieldCheck, ShieldX } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { SeverityIcon, type BimiResult } from '@/components/IssueList';

/**
 * Node's X.509 DN serializer escapes commas inside a field with a backslash, so
 * an org like "DigiCert, Inc." arrives as "DigiCert\, Inc.". Unescape `\,` → `,`
 * (and `\\` → `\`) for display.
 */
export function unescapeDn(value: string | null | undefined): string | null {
  if (!value) return null;
  return value.replace(/\\,/g, ',').replace(/\\\\/g, '\\');
}

export function BimiCard({ bimi }: { bimi: BimiResult | undefined }) {
  if (!bimi) return null;

  const hasVmc = bimi.vmcParsed === true;
  const issuer = unescapeDn(bimi.vmcIssuer);
  const mark = unescapeDn(bimi.vmcMark);

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <BadgeCheck className="h-4 w-4 text-muted-foreground" />
          BIMI &amp; Verified Mark
        </CardTitle>
        <CardDescription>
          Brand Indicators for Message Identification — your logo in the inbox, backed by a Verified
          Mark Certificate.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* BIMI presence summary */}
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {bimi.found ? (
            <Badge variant="success">BIMI record found</Badge>
          ) : (
            <Badge variant="secondary">No BIMI record</Badge>
          )}
          {bimi.found && bimi.logoUrl && (
            <a
              href={bimi.logoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
            >
              <ImageIcon className="h-3 w-3" />
              View logo
            </a>
          )}
          {bimi.displayEligible === true && <Badge variant="success">Display eligible</Badge>}
          {bimi.displayEligible === false && bimi.found && (
            <Badge variant="warn">Won&apos;t display yet</Badge>
          )}
        </div>

        {/* Verified Mark Certificate detail block */}
        {hasVmc && (
          <div className="rounded-md border bg-muted/20 p-3">
            <div className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <ShieldCheck className="h-3.5 w-3.5" />
              Verified Mark Certificate
            </div>
            <dl className="grid grid-cols-[88px_1fr] gap-x-3 gap-y-1.5 text-sm">
              <dt className="text-muted-foreground">Issuer</dt>
              <dd className="font-medium">{issuer ?? 'Unknown'}</dd>
              <dt className="text-muted-foreground">Mark holder</dt>
              <dd className="font-medium">{mark ?? 'Unknown'}</dd>
              <dt className="text-muted-foreground">Valid until</dt>
              <dd className="font-medium">
                {bimi.vmcValidTo ? new Date(bimi.vmcValidTo).toLocaleDateString() : 'Unknown'}
              </dd>
            </dl>
            <div className="mt-3 flex flex-wrap gap-2">
              {bimi.vmcIssuerApproved === true && (
                <Badge variant="success">
                  <ShieldCheck className="h-3 w-3" />
                  Approved CA
                </Badge>
              )}
              {bimi.vmcIssuerApproved === false && (
                <Badge variant="warn">
                  <ShieldX className="h-3 w-3" />
                  Unrecognized CA
                </Badge>
              )}
              {bimi.vmcHasLogotype === true && <Badge variant="success">Logotype present</Badge>}
              {bimi.vmcHasLogotype === false && <Badge variant="warn">No logotype</Badge>}
              {bimi.vmcExpired === true && <Badge variant="destructive">Expired</Badge>}
            </div>
          </div>
        )}

        {/* Evaluator issues — same rendering as every other check. */}
        {bimi.issues && bimi.issues.length > 0 && (
          <ul className="space-y-2">
            {bimi.issues.map((issue, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <SeverityIcon severity={issue.severity} />
                <span className="text-muted-foreground">{issue.message}</span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

export default BimiCard;
